<template>
  <div id="magic-map">
    <!-- Magic Map Button -->
    <b-btn
      v-if="!$store.state.dataset.file.raster && $store.state.datasetGeoJson && $store.state.datasetGeoJson.features && $store.state.datasetGeoJson.features.length > 10"
      @click="showMagicMapModal = true"
      variant="primary"
      class="mb-3"
      block
    >
      <i class="fas fa-magic"></i> Magic Map
    </b-btn>

    <!-- Magic Map Modal -->
    <b-modal
      v-model="showMagicMapModal"
      size="lg"
      :ok-disabled="!canProcess"
      @ok="processMagicMap"
      @cancel="resetForm"
      @shown="onModalShown"
    >
      <template #modal-title>
        <i class="fas fa-magic text-primary"></i> Magic Map
      </template>
      <div v-if="!isProcessing">
        <div class="mb-4">
          <p>Easily turn your dataset into an actionable zone map!</p>
        </div>
        <!-- Step 1: Block Filtering -->
        <div class="mb-4">
          <h5><i class="fas fa-crop-alt text-primary"></i> Blocks</h5>
          <b-form-checkbox v-model="settings.filterToBlocks">
            Filter dataset to block boundaries
          </b-form-checkbox>
          <div v-if="settings.filterToBlocks" class="mt-2">
            <small class="text-muted">
              This will trim the dataset to only include points within your farm's block boundaries.
            </small>
            
            <!-- No Blocks Message -->
            <div v-if="settings.filterToBlocks && farmBlocks.length === 0" class="mt-3">
              <div class="alert alert-info">
                <i class="fas fa-info-circle"></i>
                <strong>No blocks found for this farm.</strong>
                <br>
                <small>Block filtering will be skipped. The Magic Map will use the dataset boundary instead.</small>
              </div>
            </div>
            
            <!-- Block Selection Map -->
            <div v-if="settings.filterToBlocks && farmBlocks.length > 0" class="mt-3">
              <h6><i class="fas fa-map-marked-alt text-primary"></i> Select Blocks</h6>
              <p class="text-muted small">Click on blocks to select/deselect them for filtering</p>
              
              <!-- Block Selection Controls -->
              <div class="mb-2">
                <b-btn size="sm" variant="outline-primary" @click="selectAllBlocks" class="mr-2">
                  <i class="fas fa-check-square"></i> Select All
                </b-btn>
                <b-btn size="sm" variant="outline-secondary" @click="deselectAllBlocks" class="mr-2">
                  <i class="fas fa-square"></i> Deselect All
                </b-btn>
                <span class="text-muted small">
                  {{ selectedBlocksCount }} of {{ farmBlocks.length }} blocks selected
                </span>
              </div>
              
              <!-- Block Selection Map -->
              <div class="block-selection-map" ref="blockSelectionMap">
                <div class="map-container">
                  <div id="blockSelectionMapContainer" class="map-container-inner">
                    <!-- Fallback message if map doesn't load -->
                    <div v-if="!blockSelectionMap" class="map-loading-message">
                      <div class="text-center p-4">
                        <i class="fas fa-map-marked-alt fa-2x text-muted mb-2"></i>
                        <p class="text-muted">Loading interactive map...</p>
                        <small class="text-muted">If this message persists, check the browser console for errors.</small>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Map Legend -->
                  <div v-if="blockSelectionMap" class="map-legend">
                    <div class="legend-item">
                      <div class="legend-color selected-block"></div>
                      <span>Selected Blocks</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color unselected-block"></div>
                      <span>Unselected Blocks</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color dataset-point"></div>
                      <span>Dataset Points</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Selected Blocks List -->
              <div v-if="selectedBlocks.length > 0" class="mt-2">
                <h6 class="small">Selected Blocks:</h6>
                <div class="selected-blocks-list">
                  <b-badge 
                    v-for="block in selectedBlocks" 
                    :key="block.id"
                    variant="primary" 
                    class="mr-1 mb-1"
                    @click="toggleBlockSelection(block.id)"
                    style="cursor: pointer;"
                  >
                    {{ block.name || `Block ${block.id}` }}
                    <i class="fas fa-times ml-1"></i>
                  </b-badge>
                </div>
              </div>
            </div>
            
            <!-- No Blocks Message -->
            <div v-else-if="settings.filterToBlocks && farmBlocks.length === 0" class="mt-3">
              <div class="alert alert-info">
                <i class="fas fa-info-circle"></i>
                No blocks found for this farm. Block filtering will be skipped.
              </div>
            </div>
          </div>
        </div>

        <!-- Essential Settings -->
        <div class="mb-4">
          <h5><i class="fas fa-chart-line text-primary"></i> Header</h5>
          <b-form-group>
            <b-form-select v-model="settings.statisticalHeader" :options="numericHeaders" required>
              <template #first>
                <b-form-select-option :value="null" disabled>Choose a numeric header...</b-form-select-option>
              </template>
            </b-form-select>
            <small class="text-muted">This header will be used for outlier removal and visualization</small>
          </b-form-group>
        </div>

        <!-- Advanced Settings (Collapsible) -->
        <div class="mb-4">
          <b-button
            variant="outline-secondary"
            size="sm"
            class="mb-3"
            @click="showAdvanced = !showAdvanced"
          >
            <i class="fas" :class="showAdvanced ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            Advanced Settings
          </b-button>
          
          <b-collapse id="advanced-settings" :visible="showAdvanced">
            <!-- Statistical Filtering -->
            <div class="mb-4 p-3 border rounded">
              <h6><i class="fas fa-filter text-secondary"></i> Statistical Filtering</h6>
              <b-form-group label="Outlier removal method:" v-if="settings.statisticalHeader">
                <b-form-radio-group v-model="settings.outlierMethod" :options="outlierMethods" />
                <small class="text-muted d-block mt-2">
                  <div v-if="settings.outlierMethod === 'none'">
                    <strong>None:</strong> Every point inside your boundary is used as-is. Choose this when you have already trimmed the data yourself (for example to ±3 standard deviations) so Magic Map does not trim it a second time.
                  </div>
                  <div v-else-if="settings.outlierMethod === 'hdi'">
                    <strong>HDI (Highest Density Interval):</strong> Uses kernel density estimation to find the narrowest interval containing 96% of the data - mimics human "handle-dragging" to keep the main peak and remove sparse tails. Very robust to extreme values and works with any distribution shape.
                  </div>
                  <div v-else-if="settings.outlierMethod === 'two_stage_extreme_sieve'">
                    <strong>Two-Stage Extreme Sieve:</strong> First removes extreme outliers using ultra-wide quantiles (99.8% central range), then applies Adaptive IQR for final cleaning. Fast and reliable for heavily contaminated datasets with many extreme values.
                  </div>
                  <div v-else-if="settings.outlierMethod === 'adaptive_iqr'">
                    <strong>Adaptive IQR:</strong> Automatically adjusts the outlier detection threshold based on data distribution (skewness and kurtosis). More conservative for normal distributions, more aggressive for skewed data. Good balance of robustness and sensitivity.
                  </div>
                  <div v-else-if="settings.outlierMethod === 'iqr'">
                    <strong>Interquartile Range (IQR):</strong> Classic method that removes values outside 1.5 × IQR from the quartiles. Simple and widely understood, but may be too conservative for skewed distributions.
                  </div>
                  <div v-else-if="settings.outlierMethod === 'zscore'">
                    <strong>Z-Score:</strong> Removes values more than 2 standard deviations from the mean. Assumes normal distribution and may not work well with skewed data or when extreme values significantly affect the mean and standard deviation.
                  </div>
                  <div v-else-if="settings.outlierMethod === 'modified_zscore'">
                    <strong>Modified Z-Score:</strong> Uses median and median absolute deviation instead of mean and standard deviation. More robust to extreme values than standard Z-score, but still assumes roughly normal distribution.
                  </div>
                </small>
              </b-form-group>
              
              <!-- Physical Bounds Filtering -->
              <div class="mt-3" v-if="settings.statisticalHeader">
                <b-form-checkbox v-model="settings.physicalBounds.enabled">
                  <strong>Apply Physical Bounds Filtering</strong>
                </b-form-checkbox>
                <small class="text-muted d-block mb-2">
                  Set hard limits based on physical or logical constraints (e.g., yield can't be negative, pH can't exceed 14)
                </small>
                
                <div v-if="settings.physicalBounds.enabled" class="row">
                  <div class="col-md-6">
                    <b-form-group label="Minimum Value:" label-size="sm">
                      <b-form-input 
                        v-model="settings.physicalBounds.min" 
                        type="number" 
                        step="any"
                        placeholder="e.g., 0"
                        size="sm"
                      />
                      <small class="text-muted">Leave empty for no minimum</small>
                    </b-form-group>
                  </div>
                  <div class="col-md-6">
                    <b-form-group label="Maximum Value:" label-size="sm">
                      <b-form-input 
                        v-model="settings.physicalBounds.max" 
                        type="number" 
                        step="any"
                        placeholder="e.g., 14"
                        size="sm"
                      />
                      <small class="text-muted">Leave empty for no maximum</small>
                    </b-form-group>
                  </div>
                </div>
              </div>
            </div>

            <!-- Grid Generation -->
            <div class="mb-4 p-3 border rounded">
              <h6><i class="fas fa-th text-secondary"></i> Grid Generation</h6>
              <div v-if="isLargeDataset" class="alert alert-warning">
                <i class="fas fa-exclamation-triangle"></i> <strong>Large Dataset Detected:</strong> This dataset has {{ $store.state.datasetGeoJson.features.length }} points. 
                Processing may take 2-5 minutes.
              </div>
              <b-form-group label="Grid resolution (meters):">
                <b-form-input
                  v-model.number="settings.gridResolution"
                  type="number"
                  min="10"
                  max="1000"
                  step="10"
                  required
                />
                <small class="text-muted">
                  Smaller values = higher resolution but longer processing time. 
                  <strong>For large datasets (>5000 points), use 100m+ resolution to avoid timeouts.</strong>
                </small>
              </b-form-group>
            </div>

            <!-- Interpolation -->
            <div class="mb-4 p-3 border rounded">
              <h6><i class="fas fa-project-diagram text-secondary"></i> Interpolation</h6>
              <b-form-group label="Interpolation method:">
                <b-form-select v-model="settings.interpolationMethod" :options="interpolationMethods" />
              </b-form-group>
              <div v-if="settings.interpolationMethod === 'idw'">
                <b-form-group label="IDW Power:">
                  <b-form-input
                    v-model.number="settings.idwPower"
                    type="number"
                    min="0.5"
                    max="10"
                    step="0.5"
                  />
                  <small class="text-muted">Higher values keep each cell closer to the readings right around it, so highs and lows survive. Lower values average over more distant points, which smooths the map and narrows its value range. 2 is the standard choice.</small>
                </b-form-group>
              </div>
            </div>

            <!-- Visualization -->
            <div class="mb-4 p-3 border rounded">
              <h6><i class="fas fa-palette text-secondary"></i> Visualization</h6>
              <b-form-group label="Color by header:">
                <b-form-select v-model="settings.colorHeader" :options="numericHeaders" required>
                  <template #first>
                    <b-form-select-option :value="null" disabled>Choose a header...</b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
              <b-form-group label="Zone classification:">
                <b-form-select v-model="settings.zoneClassification" :options="zoneClassifications" />
              </b-form-group>
              <b-form-group label="Number of zones:">
                <b-form-input
                  v-model.number="settings.zoneCount"
                  type="number"
                  min="2"
                  max="10"
                  step="1"
                />
              </b-form-group>
            </div>

            <!-- UTM Zone Handling -->
            <div class="mb-4 p-3 border rounded">
              <h6><i class="fas fa-globe text-secondary"></i> UTM Zone Handling</h6>
              <b-form-group label="Zone Strategy:">
                <b-form-select v-model="settings.zoneStrategy" :options="zoneStrategies">
                  <template #first>
                    <b-form-select-option :value="null" disabled>Choose zone strategy...</b-form-select-option>
                  </template>
                </b-form-select>
                <small class="text-muted">
                  <strong>Primary:</strong> Use first detected zone (consistent grid)<br>
                  <strong>Split:</strong> Generate separate grids per zone (high accuracy)<br>
                  <strong>Warn:</strong> Generate with zone warnings (user awareness)
                </small>
              </b-form-group>
              <b-form-group label="Farm Center (optional):">
                <b-form-input
                  v-model="settings.farmCenter"
                  placeholder="e.g., -122.45, 37.75 (longitude, latitude)"
                  @input="validateFarmCenter"
                />
                <small class="text-muted">Use farm center as global reference point for grid alignment across multiple datasets</small>
                <div v-if="farmCenterError" class="text-danger small mt-1">{{ farmCenterError }}</div>
              </b-form-group>
            </div>
          </b-collapse>
        </div>

        <!-- Dataset Name -->
        <div class="mb-4">
          <h5><i class="fas fa-tag text-primary"></i> Output Dataset</h5>
          <b-form-group label="New dataset name:">
            <b-form-input
              v-model="settings.datasetName"
              placeholder="Enter dataset name..."
              required
            />
          </b-form-group>
          <!-- <b-form-group label="Description (optional):">
            <b-form-textarea
              v-model="settings.description"
              placeholder="Describe this interpolated dataset..."
              rows="2"
            />
          </b-form-group> -->
        </div>
      </div>

      <!-- Processing Status -->
      <div v-else>
        <div class="text-center">
          <div class="spinner-border text-primary mb-3" role="status">
            <span class="sr-only">Processing...</span>
          </div>
          <h5>Processing Magic Map...</h5>
          <p class="text-muted">{{ currentStep }}</p>
          <div class="progress mb-3">
            <div 
              class="progress-bar" 
              :style="{ width: progressPercentage + '%' }"
              role="progressbar"
            >
              {{ Math.round(progressPercentage) }}%
            </div>
          </div>
          <div v-if="processingLog.length > 0" class="text-left">
            <h6>Processing Log:</h6>
            <div class="processing-log">
              <div 
                v-for="(log, index) in processingLog" 
                :key="index"
                class="log-entry"
                :class="log.type"
              >
                <small>{{ log.timestamp }}: {{ log.message }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <div v-if="!isProcessing">
          <b-button variant="secondary" @click="resetForm" class="mr-2">Cancel</b-button>
          <b-button variant="primary" @click="processMagicMap" :disabled="!canProcess">
            <i class="fas fa-magic"></i> Create Magic Map
          </b-button>
        </div>
        <div v-else>
          <b-button variant="secondary" disabled>Processing...</b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
// import { functions } from '../../firebaseConfig.js'
const fb = require('../../firebaseConfig.js')
/* global mapboxgl */

export default {
  name: 'MagicMap',
  data() {
    return {
      showMagicMapModal: false,
      isProcessing: false,
      currentStep: '',
      progressPercentage: 0,
      processingLog: [],
      showAdvanced: false,
      settings: {
        filterToBlocks: false,
        statisticalHeader: null,
        outlierMethod: 'hdi',
        gridResolution: 3,
        interpolationMethod: 'idw',
        idwPower: 2,
        colorHeader: null,
        zoneClassification: 'jenks',
        zoneCount: 3,
        datasetName: '',
        description: '',
        zoneStrategy: 'primary',
        farmCenter: '',
        physicalBounds: {
          enabled: false,
          min: null,
          max: null
        }
      },
      farmCenterError: null,
      // Block selection properties
      farmBlocks: [],
      selectedBlockIds: [],
      blockSelectionMap: null,
      mapInitRetryCount: 0,
      outlierMethods: [
        { value: 'none', text: 'None (my data is already cleaned)' },
        { value: 'hdi', text: 'Highest Density Interval (HDI) - Human-like' },
        { value: 'two_stage_extreme_sieve', text: 'Two-Stage Extreme Sieve' },
        { value: 'adaptive_iqr', text: 'Adaptive IQR' },
        { value: 'iqr', text: 'Interquartile Range (IQR)' },
        { value: 'zscore', text: 'Z-Score (2 standard deviations)' },
        { value: 'modified_zscore', text: 'Modified Z-Score' }
      ],
      zoneStrategies: [
        { value: 'primary', text: 'Primary Zone (Consistent Grid)' },
        { value: 'split', text: 'Split by Zones (High Accuracy)' },
        { value: 'warn', text: 'Warning Mode (User Awareness)' }
      ],
      interpolationMethods: [
        { value: 'idw', text: 'Inverse Distance Weighted (IDW)' }
      ],
      zoneClassifications: [
        { value: 'jenks', text: 'Jenks Natural Breaks' },
        { value: 'equal-interval', text: 'Equal Interval' },
        { value: 'quantile', text: 'Quantile' }
      ]
    }
  },
  computed: {
    numericHeaders() {
      if (!this.$store.state.dataset.headers) return []
      
      return this.$store.state.dataset.headers.filter(header => {
        // Check if header contains numeric data
        const features = this.$store.state.datasetGeoJson.features
        if (!features || features.length === 0) return false
        
        // Sample first few features to determine if numeric
        const sampleSize = Math.min(10, features.length)
        for (let i = 0; i < sampleSize; i++) {
          const value = features[i].properties[header]
          if (value !== null && value !== undefined && !isNaN(parseFloat(value))) {
            return true
          }
        }
        return false
      }).map(header => ({
        value: header,
        text: header
      }))
    },
    canProcess() {
      return this.settings.statisticalHeader && 
             this.settings.colorHeader && 
             this.settings.datasetName.trim() !== ''
    },
    isLargeDataset() {
      return this.$store.state.datasetGeoJson && 
             this.$store.state.datasetGeoJson.features && 
             this.$store.state.datasetGeoJson.features.length > 5000
    },
    recommendedResolution() {
      if (this.isLargeDataset) {
        return Math.max(100, Math.ceil(this.$store.state.datasetGeoJson.features.length / 50))
      }
      return 50
    },
    selectedBlocksCount() {
      return this.selectedBlockIds.length
    },
    selectedBlocks() {
      return this.farmBlocks.filter(block => this.selectedBlockIds.includes(block.id))
    }
  },
  methods: {
    resetForm() {
      this.settings = {
        filterToBlocks: false,
        statisticalHeader: null,
        outlierMethod: 'hdi',
        gridResolution: 3,
        interpolationMethod: 'idw',
        idwPower: 2,
        colorHeader: null,
        zoneClassification: 'jenks',
        zoneCount: 3,
        datasetName: '',
        description: '',
        zoneStrategy: 'primary',
        farmCenter: '',
        physicalBounds: {
          enabled: false,
          min: null,
          max: null
        }
      }
      this.farmCenterError = null
      this.isProcessing = false
    },
    validateFarmCenter() {
      this.farmCenterError = null
      if (this.settings.farmCenter.trim() === '') {
        return
      }
      
      const coords = this.settings.farmCenter.split(',').map(c => c.trim())
      if (coords.length !== 2) {
        this.farmCenterError = 'Please enter coordinates as "longitude, latitude"'
        return
      }
      
      const lng = parseFloat(coords[0])
      const lat = parseFloat(coords[1])
      
      if (isNaN(lng) || isNaN(lat)) {
        this.farmCenterError = 'Please enter valid numeric coordinates'
        return
      }
      
      if (lng < -180 || lng > 180) {
        this.farmCenterError = 'Longitude must be between -180 and 180'
        return
      }
      
      if (lat < -90 || lat > 90) {
        this.farmCenterError = 'Latitude must be between -90 and 90'
      }
    },
    // Block selection methods
    async loadFarmBlocks() {
      try {
        console.log('Loading farm blocks...')
        // Check if we have a farm ID
        const farmId = this.$store.state.farm?.id
        console.log('Farm ID:', farmId)
        console.log('Full farm object:', this.$store.state.farm)
        console.log('Store state keys:', Object.keys(this.$store.state))
        if (!farmId) {
          console.error('No farm ID available for fetching blocks')
          this.farmBlocks = []
          return
        }
        
        // Load blocks from the store or fetch from Firestore
        if (this.$store.state.blocks && this.$store.state.blocks.length > 0) {
          console.log('Using blocks from store:', this.$store.state.blocks.length)
          this.farmBlocks = this.$store.state.blocks
        } else {
          console.log('Fetching blocks from Firestore...')
          console.log('Farm ID being used:', farmId)
          console.log('Farm ID type:', typeof farmId)
          
          // Fetch blocks from Firestore if not in store
          await this.$store.dispatch('fetchBlocks', farmId)
          this.farmBlocks = this.$store.state.blocks || []
          console.log('Fetched blocks:', this.farmBlocks.length)
          console.log('Store blocks after fetch:', this.$store.state.blocks?.length || 0)
          
          // Let's also try a direct Firestore query to debug
          try {
            const fb = require('../../firebaseConfig.js')
            const directQuery = await fb.blocksCollection.where('farmId', '==', farmId).get()
            console.log('Direct Firestore query result:', directQuery.size, 'documents')
            directQuery.forEach(doc => {
              console.log('Block document:', doc.id, doc.data())
            })
            
            // Let's also try to get ALL blocks to see what farmIds exist
            const allBlocksQuery = await fb.blocksCollection.limit(10).get()
            console.log('Sample of all blocks in database:')
            allBlocksQuery.forEach(doc => {
              const data = doc.data()
              console.log('Block ID:', doc.id, 'Farm ID:', data.farmId, 'Name:', data.name || 'unnamed')
            })
          } catch (error) {
            console.error('Direct Firestore query error:', error)
          }
        }
        
        // Initialize with all blocks selected by default
        this.selectedBlockIds = this.farmBlocks.map(block => block.id)
        console.log('Selected block IDs:', this.selectedBlockIds.length)
        
        // If we have blocks and the modal is shown and checkbox is checked, initialize the map
        if (this.farmBlocks.length > 0 && this.showMagicMapModal && this.settings.filterToBlocks && !this.blockSelectionMap) {
          console.log('Auto-initializing map after loading blocks...')
          this.$nextTick(() => {
            this.initializeBlockSelectionMap()
          })
        } else if (this.farmBlocks.length === 0) {
          console.log('No blocks found for this farm. Block filtering will be skipped.')
        }
      } catch (error) {
        console.error('Error loading farm blocks:', error)
        this.farmBlocks = []
      }
    },
    initializeBlockSelectionMap() {
      console.log('initializeBlockSelectionMap called')
      console.log('Farm blocks length:', this.farmBlocks.length)
      console.log('Block selection map exists:', !!this.blockSelectionMap)
      
      if (!this.farmBlocks.length || this.blockSelectionMap) {
        console.log('Skipping map initialization - no blocks or map already exists')
        return
      }
      
      try {
        // Check if Mapbox GL is available
        if (typeof mapboxgl === 'undefined') {
          console.error('Mapbox GL not available')
          return
        }
        console.log('Mapbox GL is available')
        
        // Check if the container element exists
        const container = document.getElementById('blockSelectionMapContainer')
        console.log('Container element found:', !!container)
        if (!container) {
          if (this.mapInitRetryCount < 3) {
            this.mapInitRetryCount++
            console.error(`Map container element not found, retrying in 200ms... (attempt ${this.mapInitRetryCount}/3)`)
            // Retry after a short delay
            setTimeout(() => {
              this.initializeBlockSelectionMap()
            }, 200)
            return
          } else {
            console.error('Map container element not found after 3 attempts')
            return
          }
        }
        
        // Reset retry count on successful initialization
        this.mapInitRetryCount = 0
        console.log('Container found, proceeding with map initialization...')
        
        // Set Mapbox access token
        mapboxgl.accessToken = 'pk.eyJ1IjoibGVyZ3AiLCJhIjoiY2p4bmI1NzNzMGN0MTNjcGx4cjF4eDBtdSJ9.2C0FEHhNZ-jGd7jgIRTrEQ'
        
        // Initialize Mapbox map
        console.log('Creating Mapbox map...')
        this.blockSelectionMap = new mapboxgl.Map({
          container: 'blockSelectionMapContainer',
          style: 'https://api.maptiler.com/maps/da520b6d-739a-4646-888f-1d3f2b664f78/style.json?key=IuJ0D0R9S0Cjl4krhvr1',
          center: [-95, 40], // Default center
          zoom: 10
        })
        console.log('Mapbox map created:', !!this.blockSelectionMap)
        
        // Add navigation controls
        this.blockSelectionMap.addControl(new mapboxgl.NavigationControl(), 'top-right')
        console.log('Navigation controls added')
        
        // Wait for map to load
        this.blockSelectionMap.on('load', () => {
          console.log('Map loaded, adding layers...')
          this.addDatasetToMap()
          this.addBlocksToMap()
          this.fitMapToBounds()
          console.log('Map initialization complete!')
        })
        
      } catch (error) {
        console.error('Error initializing block selection map:', error)
      }
    },
    
    addBlocksToMap() {
      console.log('addBlocksToMap called')
      console.log('Map exists:', !!this.blockSelectionMap)
      console.log('Farm blocks length:', this.farmBlocks.length)
      if (!this.blockSelectionMap || !this.farmBlocks.length) {
        console.log('Skipping addBlocksToMap - no map or blocks')
        return
      }
      
      // Create GeoJSON for all blocks
      const blocksGeoJson = {
        type: 'FeatureCollection',
        features: []
      }
      
      this.farmBlocks.forEach(block => {
        if (block.footprint) {
          try {
            const footprint = typeof block.footprint === 'string' 
              ? JSON.parse(block.footprint) 
              : block.footprint
            
            if (footprint.features && footprint.features.length > 0) {
              footprint.features.forEach(feature => {
                // Add block ID to properties for click handling
                feature.properties = {
                  ...feature.properties,
                  blockId: block.id,
                  blockName: block.name || `Block ${block.id}`
                }
                blocksGeoJson.features.push(feature)
              })
            }
          } catch (error) {
            console.error('Error parsing block footprint:', error)
          }
        }
      })
      
      if (blocksGeoJson.features.length === 0) return
      
      // Add blocks source
      this.blockSelectionMap.addSource('blocksSource', {
        type: 'geojson',
        data: blocksGeoJson
      })
      
      // Add blocks fill layer
      this.blockSelectionMap.addLayer({
        id: 'blocksFill',
        type: 'fill',
        source: 'blocksSource',
        paint: {
          'fill-color': [
            'case',
            ['in', ['get', 'blockId'], ['literal', this.selectedBlockIds]],
            'red', // Selected color
            '#6c757d' // Unselected color
          ],
          'fill-opacity': [
            'case',
            ['in', ['get', 'blockId'], ['literal', this.selectedBlockIds]],
            0.7, // Selected opacity
            0.3 // Unselected opacity
          ]
        }
      })
      
      // Add blocks outline layer
      this.blockSelectionMap.addLayer({
        id: 'blocksOutline',
        type: 'line',
        source: 'blocksSource',
        paint: {
          'line-color': [
            'case',
            ['in', ['get', 'blockId'], ['literal', this.selectedBlockIds]],
            '#0056b3', // Selected outline
            '#495057' // Unselected outline
          ],
          'line-width': 2
        }
      })
      
      // Add click handler for blocks
      this.blockSelectionMap.on('click', 'blocksFill', (e) => {
        const blockId = e.features[0].properties.blockId
        this.toggleBlockSelection(blockId)
      })
      
      // Add hover effects
      this.blockSelectionMap.on('mouseenter', 'blocksFill', () => {
        this.blockSelectionMap.getCanvas().style.cursor = 'pointer'
      })
      
      this.blockSelectionMap.on('mouseleave', 'blocksFill', () => {
        this.blockSelectionMap.getCanvas().style.cursor = ''
      })
    },
    
    addDatasetToMap() {
      console.log('addDatasetToMap called')
      console.log('Map exists:', !!this.blockSelectionMap)
      console.log('Dataset GeoJSON exists:', !!this.$store.state.datasetGeoJson)
      console.log('Dataset features:', this.$store.state.datasetGeoJson?.features?.length || 0)
      if (!this.blockSelectionMap || !this.$store.state.datasetGeoJson || !this.$store.state.datasetGeoJson.features) {
        console.log('Skipping addDatasetToMap - no map or dataset')
        return
      }
      
      const datasetGeoJson = this.$store.state.datasetGeoJson
      const geomType = datasetGeoJson.features[0]?.geometry?.type

      // Add dataset source
      this.blockSelectionMap.addSource('datasetSource', {
        type: 'geojson',
        data: datasetGeoJson
      })

      if (geomType === 'LineString' || geomType === 'MultiLineString') {
        this.blockSelectionMap.addLayer({
          id: 'datasetPoints',
          type: 'line',
          source: 'datasetSource',
          paint: {
            'line-color': 'orange',
            'line-width': 2,
            'line-opacity': 0.6
          }
        })
      } else {
        this.blockSelectionMap.addLayer({
          id: 'datasetPoints',
          type: 'circle',
          source: 'datasetSource',
          paint: {
            'circle-radius': 2,
            'circle-color': 'orange',
            'circle-opacity': 0.6,
            'circle-stroke-width': 0,
            'circle-stroke-color': '#ffffff'
          }
        })
      }
    },
    
    fitMapToBounds() {
      if (!this.blockSelectionMap || !this.farmBlocks.length) return
      
      // Calculate bounds from blocks and dataset
      const bounds = new mapboxgl.LngLatBounds()
      
      // Add blocks to bounds
      this.farmBlocks.forEach(block => {
        if (block.footprint) {
          try {
            const footprint = typeof block.footprint === 'string' 
              ? JSON.parse(block.footprint) 
              : block.footprint
            
            if (footprint.features) {
              footprint.features.forEach(feature => {
                if (feature.geometry.type === 'Polygon') {
                  feature.geometry.coordinates[0].forEach(coord => {
                    bounds.extend(coord)
                  })
                } else if (feature.geometry.type === 'MultiPolygon') {
                  feature.geometry.coordinates.forEach(polygon => {
                    polygon[0].forEach(coord => {
                      bounds.extend(coord)
                    })
                  })
                }
              })
            }
          } catch (error) {
            console.error('Error calculating bounds from block:', error)
          }
        }
      })
      
      // Add dataset features to bounds
      if (this.$store.state.datasetGeoJson && this.$store.state.datasetGeoJson.features) {
        this.$store.state.datasetGeoJson.features.forEach(feature => {
          const geom = feature.geometry
          if (geom.type === 'Point') {
            bounds.extend(geom.coordinates)
          } else if (geom.type === 'LineString' && geom.coordinates) {
            geom.coordinates.forEach(coord => bounds.extend(coord))
          } else if (geom.type === 'MultiLineString' && geom.coordinates) {
            geom.coordinates.forEach(line => {
              line.forEach(coord => bounds.extend(coord))
            })
          }
        })
      }
      
      // Fit map to bounds (jump instead of fly)
      if (!bounds.isEmpty()) {
        this.blockSelectionMap.fitBounds(bounds, { 
          padding: 50,
          duration: 0 // Jump immediately instead of flying
        })
      }
    },
    toggleBlockSelection(blockId) {
      const index = this.selectedBlockIds.indexOf(blockId)
      if (index > -1) {
        this.selectedBlockIds.splice(index, 1)
      } else {
        this.selectedBlockIds.push(blockId)
      }
      
      // Update map styling
      this.updateMapBlockStyling()
    },
    selectAllBlocks() {
      this.selectedBlockIds = this.farmBlocks.map(block => block.id)
      this.updateMapBlockStyling()
    },
    deselectAllBlocks() {
      this.selectedBlockIds = []
      this.updateMapBlockStyling()
    },
    updateMapBlockStyling() {
      if (!this.blockSelectionMap) return
      
      // Update block styling using Mapbox expressions
      this.blockSelectionMap.setPaintProperty('blocksFill', 'fill-color', [
        'case',
        ['in', ['get', 'blockId'], ['literal', this.selectedBlockIds]],
        'red', // Selected color
        '#6c757d' // Unselected color
      ])
      
      this.blockSelectionMap.setPaintProperty('blocksFill', 'fill-opacity', [
        'case',
        ['in', ['get', 'blockId'], ['literal', this.selectedBlockIds]],
        0.7, // Selected opacity
        0.3 // Unselected opacity
      ])
      
      this.blockSelectionMap.setPaintProperty('blocksOutline', 'line-color', [
        'case',
        ['in', ['get', 'blockId'], ['literal', this.selectedBlockIds]],
        'red', // Selected outline
        '#495057' // Unselected outline
      ])
    },
    cleanupBlockSelectionMap() {
      if (this.blockSelectionMap) {
        this.blockSelectionMap.remove()
        this.blockSelectionMap = null
      }
    },
    async processMagicMap() {
      this.isProcessing = true
      this.processingLog = []
      this.progressPercentage = 0
      
      try {
        // Validate farm center if provided
        if (this.settings.farmCenter.trim() !== '') {
          this.validateFarmCenter()
          if (this.farmCenterError) {
            throw new Error(this.farmCenterError)
          }
        }
        
        // Prepare the request data
        const requestData = {
          datasetId: this.$store.state.dataset.id,
          farmId: this.$store.state.farm.id,
          userId: this.$store.state.currentUser.uid,
          settings: this.settings,
          datasetGeoJson: this.$store.state.datasetGeoJson
        }
        
        // Add farm center if provided
        if (this.settings.farmCenter.trim() !== '') {
          const coords = this.settings.farmCenter.split(',').map(c => parseFloat(c.trim()))
          requestData.farmCenter = coords // [lng, lat]
        }
        
        // Add selected blocks if filtering to blocks
        if (this.settings.filterToBlocks && this.selectedBlockIds.length > 0) {
          requestData.selectedBlocks = this.selectedBlocks.map(block => ({
            id: block.id,
            name: block.name,
            geometry: block.footprint
          }))
        }
        
        // Start progress tracking
        this.startProgressTracking()
        
        // Call the cloud function
        console.log('Making request to processMagicMap...')
        this.updateProgress('Connecting to server...', 5)
        
        // Show progress during the long-running process
        const progressInterval = setInterval(() => {
          if (this.progressPercentage < 85) {
            this.progressPercentage += Math.random() * 10 // Random increment
            if (this.progressPercentage > 85) this.progressPercentage = 85
            
            // Update message based on progress
            if (this.progressPercentage < 20) {
              this.currentStep = 'Creating boundary for grid trimming...'
            } else if (this.progressPercentage < 40) {
              this.currentStep = 'Filtering data to block boundaries...'
            } else if (this.progressPercentage < 60) {
              this.currentStep = 'Applying statistical filtering...'
            } else if (this.progressPercentage < 80) {
              this.currentStep = 'Running IDW interpolation...'
            } else {
              this.currentStep = 'Generating interpolated grid...'
            }
          }
        }, 2000) // Update every 2 seconds
        
        // processMagicMap now requires a verified Firebase ID token —
        // see functions/authMiddleware.js. The Bearer header is in
        // addition to the body's farmId; the server checks that the
        // token's uid has at least Editor on that farm.
        const idToken = await fb.auth.currentUser.getIdToken()
        const response = await fetch('https://us-central1-efficient-vineyard.cloudfunctions.net/processMagicMap', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify(requestData)
        })
        
        // Clear the progress interval once we get a response
        clearInterval(progressInterval)
        
        console.log('Response received:', response.status, response.statusText)
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        // Show that we're processing the response
        this.updateProgress('Processing interpolation results...', 90)
        
        const result = await response.json()
        
        if (result.success) {
          // Show completion steps
          this.updateProgress('Saving dataset...', 95)
          await new Promise(resolve => setTimeout(resolve, 500)) // Brief pause
          
          this.updateProgress('Finishing up...', 98)
          await new Promise(resolve => setTimeout(resolve, 300)) // Brief pause
          
          this.updateProgress('Complete!', 100)
          await new Promise(resolve => setTimeout(resolve, 500)) // Brief pause
          
          // Handle UTM zone information
          let alertMessage = 'Magic Map created successfully!'
          let alertLevel = 'alert-success'
          
          if (result.gridMetadata) {
            const { totalPoints, zones, crossesZones, extremeIntersection } = result.gridMetadata
            
            alertMessage += ` Generated ${totalPoints} grid points.`
            
            if (crossesZones) {
              alertMessage += ` Detected UTM zones: ${zones.join(', ')}.`
              alertLevel = 'alert-warning'
            }
            
            if (extremeIntersection) {
              alertMessage += ' ⚠️ Extreme UTM zone intersection detected.'
              alertLevel = 'alert-warning'
            }
          }
          
          // What the trimming and smoothing did — the same sentence is saved
          // in the new dataset's description.
          if (result.dataPrepSummary && result.dataPrepSummary.text) {
            alertMessage += ` ${result.dataPrepSummary.text}`
          }

          // Show warnings if present
          if (result.warning) {
            this.$store.commit('setAlert', {
              active: true,
              message: `Warning: ${result.warning}`,
              level: 'alert-warning',
              timeout: 8000
            })
          }
          
          if (result.extremeWarning) {
            this.$store.commit('setAlert', {
              active: true,
              message: result.extremeWarning,
              level: 'alert-warning',
              timeout: 10000
            })
          }
          
          this.$store.commit('setAlert', {
            active: true,
            message: alertMessage,
            level: alertLevel,
            timeout: 20000
          })
          
          // Refresh the farm data to show the new dataset
          await this.$store.dispatch('refreshFarm')
          
          // Launch the new dataset if we have the dataset ID
          if (result.datasetId) {
            this.updateProgress('Launching new dataset...', 100)
            await new Promise(resolve => setTimeout(resolve, 500)) // Brief pause
            
            // Navigate to the new dataset
            // this.$router.push(`/f/${this.$store.state.farm.id}/dataset/${result.datasetId}`)
            console.log('Launching new dataset...', result.datasetId)
            const datasetDoc = await fb.datasetsCollection.doc(result.datasetId).get()
            const dataset = datasetDoc.data()
            dataset.id = datasetDoc.id
            this.$store.dispatch('launchDataset', {
              dataset: dataset
            })
          }
          
          // Close the modal
          this.showMagicMapModal = false
          this.resetForm()
        } else {
          throw new Error(result.error || 'Unknown error occurred')
        }
        
      } catch (error) {
        console.error('Magic Map processing error:', error)
        this.updateProgress('Error occurred', 0)
        this.$store.commit('setAlert', {
          active: true,
          message: 'Error creating Magic Map: ' + error.message,
          level: 'alert-danger',
          timeout: 10000
        })
      } finally {
        this.isProcessing = false
      }
    },
    startProgressTracking() {
      this.currentStep = 'Starting...'
      this.progressPercentage = 0
      this.processingLog = []
      
      this.addLogEntry('Starting Magic Map processing...', 'info')
    },
    
    updateProgress(message, percentage) {
      this.currentStep = message
      this.progressPercentage = percentage
      this.addLogEntry(message, 'info')
    },
    
    addLogEntry(message, type = 'info') {
      this.processingLog.push({
        timestamp: new Date().toLocaleTimeString(),
        message: message,
        type: type
      })
    },
    onModalShown() {
      // Modal is fully shown, now we can safely initialize the map
      console.log('Modal shown, checking map initialization conditions...')
      console.log('Farm blocks length:', this.farmBlocks.length)
      console.log('Block selection map exists:', !!this.blockSelectionMap)
      console.log('Filter to blocks setting:', this.settings.filterToBlocks)
      
      // Only initialize map if checkbox is checked
      if (this.settings.filterToBlocks) {
        // Initialize map if we have blocks and no existing map
        if (this.farmBlocks.length > 0 && !this.blockSelectionMap) {
          console.log('Initializing block selection map...')
          this.$nextTick(() => {
            this.initializeBlockSelectionMap()
          })
        } else if (this.farmBlocks.length === 0) {
          console.log('No farm blocks available, loading blocks first...')
          this.loadFarmBlocks()
        } else if (this.blockSelectionMap) {
          console.log('Map already exists')
        } else {
          console.log('Map initialization conditions not met')
        }
      } else {
        console.log('Filter to blocks not checked, skipping map initialization')
      }
    }
  },
  watch: {
    'settings.filterToBlocks'(newValue) {
      console.log('Filter to blocks changed:', newValue)
      if (newValue) {
        // Load blocks if not already loaded
        if (this.farmBlocks.length === 0) {
          console.log('Loading blocks for first time...')
          this.loadFarmBlocks()
        } else if (this.farmBlocks.length > 0) {
          // Blocks already loaded, initialize map if modal is shown
          console.log('Blocks already loaded, initializing map...')
          this.$nextTick(() => {
            if (this.showMagicMapModal && !this.blockSelectionMap) {
              this.initializeBlockSelectionMap()
            }
          })
        } else {
          console.log('No blocks available for this farm')
        }
      } else {
        // Clean up map when checkbox is unchecked
        console.log('Cleaning up map...')
        this.cleanupBlockSelectionMap()
      }
    },
    showMagicMapModal(newValue) {
      if (newValue) {
        // Reset retry count when modal opens
        this.mapInitRetryCount = 0
        console.log('Modal opened, not loading blocks automatically')
      } else {
        // Clean up map when modal closes
        this.cleanupBlockSelectionMap()
      }
    },
    'settings.statisticalHeader'(newValue) {
      // Auto-select the same header for color visualization
      if (newValue && !this.settings.colorHeader) {
        this.settings.colorHeader = newValue
      }
    }
  },
  mounted() {
    // Set default dataset name
    if (this.$store.state.dataset.name) {
      this.settings.datasetName = this.$store.state.dataset.name + ' - Magic Map'
    }
  },
  beforeDestroy() {
    // Clean up map when component is destroyed
    this.cleanupBlockSelectionMap()
  }
}
</script>

<style scoped>
.text-primary, .text-primary:hover {
  color: #056839 !important;
}

button.btn-primary {
  background-color: #056839 !important;
}

.btn-outline-primary {
  color: #056839 !important;
  border-color: #056839 !important;
}

/* Custom checkbox styling to match green theme */
.custom-control-input:checked ~ .custom-control-label::before {
  border-color: #056839 !important;
  background-color: #056839 !important;
}

/* Bootstrap 4 checkbox styling */
.form-check-input:checked {
  background-color: #056839 !important;
  border-color: #056839 !important;
}

/* Bootstrap 5 checkbox styling */
.form-check-input:checked {
  background-color: #056839 !important;
  border-color: #056839 !important;
}

/* Custom checkbox for Vue Bootstrap */
.custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #056839 !important;
  border-color: #056839 !important;
}

/* Ensure all checkboxes use the green color */
input[type="checkbox"]:checked {
  background-color: #056839 !important;
  border-color: #056839 !important;
}

/* Override any remaining blue colors with green */
.btn-primary {
  background-color: #056839 !important;
  border-color: #056839 !important;
}

.btn-primary:hover {
  background-color: #044a2a !important;
  border-color: #044a2a !important;
}

.btn-primary:focus {
  background-color: #056839 !important;
  border-color: #056839 !important;
  box-shadow: 0 0 0 0.2rem rgba(5, 104, 57, 0.25) !important;
}

/* Progress bar color */
.progress-bar {
  background-color: #056839 !important;
}

/* Any other blue elements */
.text-primary {
  color: #056839 !important;
}

.badge-primary {
  background-color: #056839 !important;
}

.processing-log {
  max-height: 200px;
  overflow-y: auto;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 0.5rem;
}

.log-entry {
  margin-bottom: 0.25rem;
}

.log-entry.info {
  color: #0066cc;
}

.log-entry.success {
  color: #056839;
}

.log-entry.error {
  color: #dc3545;
}

.log-entry.warning {
  color: #ffc107;
}

/* Block Selection Map Styles */
.block-selection-map {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  overflow: hidden;
  margin-bottom: 1rem;
}

.map-container {
  position: relative;
  width: 100%;
  height: 300px;
}

.map-container-inner {
  width: 100%;
  height: 100%;
  position: relative;
}

.map-loading-message {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
}

.selected-blocks-list {
  max-height: 100px;
  overflow-y: auto;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 0.5rem;
}

/* Map Legend */
.map-legend {
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(255, 255, 255, 0.9);
  padding: 10px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-size: 12px;
  z-index: 1000;
}

.legend-item {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

.legend-item:last-child {
  margin-bottom: 0;
}

.legend-color {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  border-radius: 2px;
  border: 1px solid #ccc;
}

.legend-color.selected-block {
  background-color: red;
  opacity: 0.7;
}

.legend-color.unselected-block {
  background-color: #6c757d;
  opacity: 0.3;
}

.legend-color.dataset-point {
  background-color: orange;
  border-radius: 50%;
  width: 8px;
  height: 8px;
  opacity: 0.6;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .map-container {
    height: 250px;
  }
  
  .block-selection-map {
    margin-bottom: 0.5rem;
  }
  
  .map-legend {
    font-size: 11px;
    padding: 8px;
  }
  
  .legend-color {
    width: 14px;
    height: 14px;
  }
  
  .legend-color.dataset-point {
    width: 6px;
    height: 6px;
  }
}
</style>
